import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import store from '@/store'
import {trackStatisticList} from '@/config/index'
import {getSystemTime} from '@/utils/index'
import {trackStatistic } from '@/service'
import { tokenStr,AUTH } from '@/config'
import {
  getSystemHomePath,
  PILOT_ASSESSMENT_ROOT,
  resolveSystemMode,
  SystemMode,
} from '@/types/system'

const router = createRouter({
  history: createWebHistory(),
  routes
})

const LOGIN_SYSTEM_MODE_KEY = 'loginSystemMode'
const PILOT_ASSESSMENT_TRAINING_CONTEXT_KEY = 'pilotAssessmentTrainingContext'
const PILOT_ASSESSMENT_FLIGHT_PATHS = new Set([
  PILOT_ASSESSMENT_ROOT + '/flight/exam',
  PILOT_ASSESSMENT_ROOT + '/flight/transmit',
])

const canKeepThirdSceneSelection = (path: string): boolean => {
  return path === '/flight/train' || path === '/flight/passTest' || path === '/flight/transmit';
};

const isBrowserReload = (): boolean => {
  const navigationEntries = window.performance?.getEntriesByType('navigation') || []
  const navigationEntry = navigationEntries[0] as PerformanceNavigationTiming | undefined
  if (navigationEntry) return navigationEntry.type === 'reload'

  const legacyPerformance = window.performance as Performance & {
    navigation?: { type?: number }
  }
  return legacyPerformance.navigation?.type === 1
}

const getPilotAssessmentRouteContext = (): {
  sessionId: string
  roundId: string
} | null => {
  const value = sessionStorage.getItem(PILOT_ASSESSMENT_TRAINING_CONTEXT_KEY)
  if (!value) return null
  try {
    const context = JSON.parse(value) as {
      sessionId?: string | number
      roundId?: string | number
    }
    if (!context.sessionId || !context.roundId) {
      sessionStorage.removeItem(PILOT_ASSESSMENT_TRAINING_CONTEXT_KEY)
      return null
    }
    return {
      sessionId: String(context.sessionId),
      roundId: String(context.roundId),
    }
  } catch {
    sessionStorage.removeItem(PILOT_ASSESSMENT_TRAINING_CONTEXT_KEY)
    return null
  }
}

router.beforeEach(async (to, from, next) => {
  const routeSystemMode =
    (to.meta.systemMode as SystemMode | undefined) || resolveSystemMode(to.path)
  store.commit('viewData/setSystemMode', routeSystemMode)

  if (to.query?.accessToken) {
    localStorage.setItem('Authorization', to.query.accessToken as string)
    const storedSystemMode = sessionStorage.getItem(LOGIN_SYSTEM_MODE_KEY)
    const loginSystemMode =
      storedSystemMode === SystemMode.PILOT_ASSESSMENT
        ? SystemMode.PILOT_ASSESSMENT
        : storedSystemMode === SystemMode.ONLINE_TRAINING
          ? SystemMode.ONLINE_TRAINING
          : routeSystemMode
    sessionStorage.removeItem(LOGIN_SYSTEM_MODE_KEY)
    sessionStorage.removeItem('params')
    next({
      path: getSystemHomePath(loginSystemMode),
      replace: true,
    })
    return
  }

  const token = localStorage.getItem('Authorization')
  if (!token) {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('Authorization', tokenStr)
      next({
        path: getSystemHomePath(routeSystemMode),
        replace: true,
      })
      return
    }
    sessionStorage.setItem(LOGIN_SYSTEM_MODE_KEY, routeSystemMode)
    window.location.replace(window.location.origin + AUTH + '/login')
    return
  }

  if (PILOT_ASSESSMENT_FLIGHT_PATHS.has(to.path) && !getPilotAssessmentRouteContext()) {
    next({
      name: 'CandidateHome',
      replace: true,
    })
    return
  }

  const isInitialNavigation = from.matched.length === 0
  if (
    isInitialNavigation &&
    isBrowserReload() &&
    PILOT_ASSESSMENT_FLIGHT_PATHS.has(to.path)
  ) {
    const context = getPilotAssessmentRouteContext()
    if (context) {
      next({
        name: 'CandidateSubjects',
        params: { sessionId: context.sessionId },
        query: { roundId: context.roundId },
        replace: true,
      })
      return
    }
  }

  //从caac飞行页面里面离开的时候删除trainingId
  if (['train', 'passTest', 'exam', 'guide'].includes(String(from.meta.flightType || ''))) {
    store.commit('persistence/setCurrentUavInfo', {
      ...store.state.persistence.currentUavInfo,
      trainingId: null
    });
  }

  // thirdScene: 仅跳转到 /flight/train /flight/passTest /flight/transmit 时保留选中
  const breadcrumb = store.state.persistence.breadcrumb || [];
  const currentPage = breadcrumb.length ? breadcrumb[breadcrumb.length - 1].currentPage : null;
  const fromThirdSceneComponent = from.path === '/' && currentPage === 3;
  if (fromThirdSceneComponent && !canKeepThirdSceneSelection(to.path)) {
    store.commit('persistence/setCarouselSelection', {
      ...store.state.persistence.carouselSelection,
      thirdSceneCardId: null
    });
  }

  // tower: 仅跳转到 /flight/towerView 时保留选中
  if (from.path.includes('/secondary/tower') && !to.path.includes('/flight/towerView')) {
    store.commit('persistence/setCarouselSelection', {
      ...store.state.persistence.carouselSelection,
      towerCardId: null
    });
  }

  if (trackStatisticList.includes(to.fullPath)) {
    const eventTime = getSystemTime();
    const { appId, userId } = store.state.persistence.userInfo;
    trackStatistic([{ appId, userId, eventKey: "simulator_page_view", eventTime, params: { page: to.fullPath } }]);
  }
  next();
})
export default router
